// Message operations service for handling message sending, keyboard interactions, and textarea functionality
import React from 'react';
import { Model, BillingSource, UserTeamSummary } from '../../types';
import { MessageWithCitations, StoredSuggestions } from './chatTypes';
import { storeFile, retrieveFile } from './fileDatabase';
import { isImageGenerationModel, sanitizeFilename } from './chatUtils';
import config from '../../config';
import mixpanel from 'mixpanel-browser';

const BASE_URL = config.apiUrl;

export interface MessageOperationsConfig {
  isMobile: boolean;
  currentConversationId: number | null;
  selectedFile: File | null;
  selectedFileName: string | null;
  userInfo?: {
    name: string;
    email: string;
    status: string;
    credits_used: number;
    subscription_type?: string;
    // Team-aware credit fields from /auth/credit-info (see src/types.ts User).
    credits_total?: number | null;
    billing_source?: BillingSource;
    team?: UserTeamSummary | null;
    raw_status?: string;
    has_team?: boolean;
  } | null;
  navigationData?: { id: number; uuid: string };
}

export interface MessageOperationsCallbacks {
  setCurrentConversationId: (id: number | null) => void;
  setConversations: React.Dispatch<React.SetStateAction<any[]>>;
  setMessages: React.Dispatch<React.SetStateAction<MessageWithCitations[]>>;
  setInputMessage: (message: string) => void;
  setIsLoading: (loading: boolean) => void;
  setSelectedFile: (file: File | null) => void;
  setSelectedFileName: (name: string | null) => void;
  setShouldScroll: (should: boolean) => void;
  setIsFirstMessageLoading: (loading: boolean) => void;
  navigate: (path: string) => void;
  getModelDisplayName: (model: Model) => string;
}

// Keyboard event handler for textarea
export const handleKeyPress = (
  e: React.KeyboardEvent<HTMLTextAreaElement>,
  isMobile: boolean,
  handleSubmit: (e: React.FormEvent) => void
) => {
  // Only send on Enter if not on mobile
  if (!isMobile && e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSubmit(e as any);
  }
};

// Auto-resize textarea based on content
export const handleTextAreaHeight = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  const textarea = e.target;
  textarea.style.height = 'inherit';
  
  // Calculate content height
  const computed = window.getComputedStyle(textarea);
  const height = parseInt(computed.getPropertyValue('border-top-width'), 10)
                 + parseInt(computed.getPropertyValue('padding-top'), 10)
                 + textarea.scrollHeight
                 + parseInt(computed.getPropertyValue('padding-bottom'), 10)
                 + parseInt(computed.getPropertyValue('border-bottom-width'), 10);
  
  // Set height with max limit
  const maxHeight = 150; // Match with CSS max-height
  textarea.style.height = `${Math.min(height, maxHeight)}px`;
  
  // Add scrollbar class if content exceeds max height
  if (height > maxHeight) {
    textarea.style.overflowY = 'scroll';
  } else {
    textarea.style.overflowY = 'hidden';
  }
};

// Reset textarea to initial height
export const resetTextAreaHeight = () => {
  const textarea = document.querySelector('textarea');
  if (textarea) {
    textarea.style.height = '24px'; // Reset to initial height
    textarea.style.overflowY = 'hidden';
  }
};

// Create a new conversation
async function createConversation(
  messageToUse: string,
  config: MessageOperationsConfig,
  callbacks: MessageOperationsCallbacks
): Promise<number | null> {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType');

  try {
    callbacks.setIsFirstMessageLoading(true);
    
    // Get current background and persona values
    const currentBackground = localStorage.getItem('userBackground') || '';
    const currentPersona = localStorage.getItem('assistantPersona') || '';

    const response = await fetch(`${BASE_URL}/conversations/`, {
      method: 'POST',
      headers: {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: messageToUse.length > 30 
          ? messageToUse.substring(0, 30) + '...'
          : messageToUse
      })
    });

    if (response.ok) {
      const data = await response.json();
      const conversationId = data.id;
      
      callbacks.setCurrentConversationId(conversationId);
      localStorage.setItem('lastConversationId', conversationId.toString());
      localStorage.setItem('lastConversationUuid', data.uuid);

      // Navigate to the new conversation
      config.navigationData = { id: conversationId, uuid: data.uuid };
      window.history.pushState({}, '', `/c/${data.uuid}`);

      // Store background and persona for the new conversation
      const storedSuggestions: StoredSuggestions = JSON.parse(
        localStorage.getItem('conversationSuggestions') || '{}'
      );
      storedSuggestions[conversationId] = {
        analysis: '',
        message_count: 0,
        analyzed_at: new Date().toISOString(),
        background: currentBackground,
        persona: currentPersona
      };
      localStorage.setItem('conversationSuggestions', JSON.stringify(storedSuggestions));

      callbacks.setConversations(prev => [{
        id: conversationId,
        uuid: data.uuid,
        title: data.title,
        created_at: data.created_at,
        updated_at: data.updated_at
      }, ...prev]);

      return conversationId;
    }
  } catch (error) {
    console.error('Error creating conversation:', error);
    return null;
  } finally {
    callbacks.setIsFirstMessageLoading(false);
  }
  
  return null;
}

// Track message analytics
function trackMessageAnalytics(
  modelToUse: Model,
  messageToUse: string,
  config: MessageOperationsConfig,
  callbacks: MessageOperationsCallbacks
) {
  // Track message sent
  mixpanel.track('Message Sent', {
    model: modelToUse,
    has_file: !!config.selectedFile,
    message_length: messageToUse.length,
    conversation_id: config.currentConversationId,
    is_first_message: !config.currentConversationId,
    user_email: config.userInfo?.email,
    user_status: config.userInfo?.status,
    timestamp: new Date().toISOString()
  });
  
  // Increment user message count
  mixpanel.people.increment('Total Messages Sent');
  
  // Increment model usage counter
  mixpanel.people.increment(`Model: ${modelToUse}`);
  
  // Track model usage over time
  mixpanel.track('Model Used', {
    model: modelToUse,
    model_display_name: callbacks.getModelDisplayName(modelToUse),
    conversation_id: config.currentConversationId,
    user_email: config.userInfo?.email,
    user_status: config.userInfo?.status
  });
}

// Handle image generation workflow
async function handleImageGeneration(
  messageToUse: string,
  modelToUse: Model,
  config: MessageOperationsConfig,
  callbacks: MessageOperationsCallbacks,
  textareaRef: React.RefObject<HTMLTextAreaElement>
): Promise<void> {
  try {
    // Store the reference image in IndexedDB if exists
    if (config.selectedFile && config.selectedFileName) {
      const referenceFile = new File([config.selectedFile], config.selectedFileName, { 
        type: config.selectedFile.type 
      });
      await storeFile(config.selectedFileName, referenceFile);
    }

    // Create user message first with file if exists
    const userMessage: MessageWithCitations = {
      id: Date.now(), // Temporary ID
      role: 'user',
      content: config.selectedFile && config.selectedFileName
        ? `${messageToUse} [File: ${sanitizeFilename(config.selectedFile.name)}](file:${config.selectedFileName})`
        : messageToUse,
      model: modelToUse,
      fileUrl: config.selectedFile ? URL.createObjectURL(config.selectedFile) : undefined,
      isComplete: true, // User messages are inherently complete
      created_at: new Date().toISOString()
    };

    // Update messages with user message
    callbacks.setMessages(prev => [...prev, userMessage]);
    
    // Create initial assistant message with loading state
    const assistantMessage: MessageWithCitations = {
      id: Date.now() + 1, // Temporary ID
      role: 'assistant',
      content: '',
      model: modelToUse,
      isComplete: false,
      fileUrl: 'generating' // Special flag to show loading animation
    };
    
    // Update messages with loading state
    callbacks.setMessages(prev => [...prev, assistantMessage]);
    
    // Reset input and UI state
    callbacks.setInputMessage('');
    callbacks.setIsLoading(true);
    resetTextAreaHeight();
    
    // Focus textarea after a brief delay
    setTimeout(() => {
      textareaRef.current?.focus();
      callbacks.setInputMessage('');
      callbacks.setSelectedFile(null);
      callbacks.setSelectedFileName(null);
      callbacks.setIsLoading(true);
      resetTextAreaHeight();
      callbacks.setShouldScroll(false);
    }, 100);

  } catch (error) {
    console.error('Error in image generation workflow:', error);
    callbacks.setIsLoading(false);
  }
}

// Main message submission handler
export async function handleSubmit(
  e: React.FormEvent,
  inputMessage: string,
  selectedModel: Model,
  config: MessageOperationsConfig,
  callbacks: MessageOperationsCallbacks,
  textareaRef: React.RefObject<HTMLTextAreaElement>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  forcedModel?: Model,
  forcedMessage?: string
): Promise<void> {
  if (e) e.preventDefault();
  
  const modelToUse = forcedModel ?? selectedModel;
  const messageToUse = forcedMessage ?? inputMessage;
  
  if (!forcedModel && (!messageToUse.trim() || config.currentConversationId === null && callbacks.setIsLoading)) return;
  
  // Add startTime to measure processing time
  const startTime = Date.now();
  
  // Track analytics
  trackMessageAnalytics(modelToUse, messageToUse, config, callbacks);
  
  abortControllerRef.current = new AbortController();

  // Check if this is the first message in the conversation
  const isFirstMessage = !config.currentConversationId;
  let conversationId = config.currentConversationId;

  if (isFirstMessage) {
    conversationId = await createConversation(messageToUse, config, callbacks);
    if (!conversationId) {
      return; // Failed to create conversation
    }
    // Update config with new conversation ID
    config.currentConversationId = conversationId;
  }

  // Proceed with sending the message
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType');
  if (!token) {
    callbacks.navigate('/login');
    return;
  }
  
  // Handle image generation models differently
  if (isImageGenerationModel(modelToUse)) {
    await handleImageGeneration(messageToUse, modelToUse, config, callbacks, textareaRef);
    return;
  }

  // Continue with regular message handling...
  // Note: This would continue with the rest of the handleSubmit logic
  // For now, this shows the structure for the extracted functions
}